import { lazy, Suspense } from 'react';
import type { AskAiCellValue, AskAiChartHint } from '@/types/askAi';
import { AskAiChartLegacy } from '@/components/ai/AskAiChart';
import { rowsToObjects } from './chartData';

const TopNBarChart = lazy(() => import('./TopNBarChart'));
const TimeSeriesChart = lazy(() => import('./TimeSeriesChart'));
const ShareDonutChart = lazy(() => import('./ShareDonutChart'));
const HodDowHeatmap = lazy(() => import('./HodDowHeatmap'));

interface Props {
  columns: string[];
  rows: AskAiCellValue[][];
  hint: AskAiChartHint;
}

/**
 * Server-owned template_id -> component switch (mirrors
 * services/llm/chart_templates.py::CHART_TEMPLATES). Falls back to the
 * legacy free-form {type,x,y} renderer when the server emits the older
 * chart_hint shape instead of {template_id, params}.
 */
export function ChartDispatcher({ columns, rows, hint }: Props) {
  if (hint.template_id) {
    const params = hint.params ?? {};
    const data = rowsToObjects(columns, rows);

    let element: React.ReactNode = null;
    switch (hint.template_id) {
      case 'top_n_bar':
        if (!params.x_field || !params.y_field) return null;
        element = (
          <TopNBarChart data={data} x_field={params.x_field} y_field={params.y_field} n={params.n} />
        );
        break;
      case 'time_series':
        if (!params.x_field || !params.y_field) return null;
        element = (
          <TimeSeriesChart
            data={data}
            x_field={params.x_field}
            y_field={params.y_field}
            agg={params.agg}
          />
        );
        break;
      case 'share_donut':
        if (!params.label_field || !params.value_field) return null;
        element = (
          <ShareDonutChart
            data={data}
            label_field={params.label_field}
            value_field={params.value_field}
            top_n={params.top_n}
          />
        );
        break;
      case 'hod_dow_heatmap':
        if (!params.time_field || !params.value_field) return null;
        element = (
          <HodDowHeatmap
            data={data}
            time_field={params.time_field}
            value_field={params.value_field}
            midnight_excluded_count={params.midnight_excluded_count}
          />
        );
        break;
      default:
        return null;
    }

    return <Suspense fallback={null}>{element}</Suspense>;
  }

  if (hint.type && hint.x && hint.y) {
    return (
      <AskAiChartLegacy columns={columns} rows={rows} hint={{ type: hint.type, x: hint.x, y: hint.y }} />
    );
  }

  return null;
}

export default ChartDispatcher;
